<template>
    <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-800">
                <span class="mr-2">👨‍🏫</span>
                Faculty Course Load Summary
            </h3>
        </div>

        <div v-if="loading" class="flex justify-center items-center h-64">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>

        <div v-else-if="error" class="text-center text-red-600 py-8">
            <p>Error loading data</p>
        </div>

        <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Faculty with Most Courses -->
            <div class="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4">
                <div class="flex items-center mb-3">
                    <div class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                        <span class="text-white text-sm font-bold">📈</span>
                    </div>
                    <h4 class="text-md font-semibold text-green-800">Most Courses</h4>
                </div>

                <div v-if="!mostCourses || mostCourses.length === 0" class="text-gray-500 text-sm">
                    No data available
                </div>

                <div v-else class="space-y-2">
                    <div v-for="(faculty, index) in mostCourses" :key="index"
                        class="flex justify-between items-center bg-white rounded-md p-2 shadow-sm">
                        <div class="flex-1">
                            <p class="text-sm font-medium text-gray-900">{{ faculty.name }}</p>
                            <p class="text-xs text-gray-600">{{ faculty.program }}</p>
                        </div>
                        <div class="text-right">
                            <span
                                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {{ faculty.course_count }} courses
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Faculty with Least Courses -->
            <div class="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4">
                <div class="flex items-center mb-3">
                    <div class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                        <span class="text-white text-sm font-bold">📉</span>
                    </div>
                    <h4 class="text-md font-semibold text-blue-800">Least Courses</h4>
                </div>

                <div v-if="!leastCourses || leastCourses.length === 0" class="text-gray-500 text-sm">
                    No data available
                </div>

                <div v-else class="space-y-2">
                    <div v-for="(faculty, index) in leastCourses" :key="index"
                        class="flex justify-between items-center bg-white rounded-md p-2 shadow-sm">
                        <div class="flex-1">
                            <p class="text-sm font-medium text-gray-900">{{ faculty.name }}</p>
                            <p class="text-xs text-gray-600">{{ faculty.program }}</p>
                        </div>
                        <div class="text-right">
                            <span
                                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {{ faculty.course_count }} courses
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Summary Statistics -->
        <div v-if="!loading && !error && ((mostCourses && mostCourses.length > 0) || (leastCourses && leastCourses.length > 0))"
            class="mt-6 pt-4 border-t border-gray-200">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div class="bg-gray-50 rounded-lg p-3">
                    <p class="text-sm text-gray-600">Highest Course Load</p>
                    <p class="text-lg font-semibold text-gray-900">
                        {{ (mostCourses && mostCourses[0] && mostCourses[0].course_count) || 0 }} courses
                    </p>
                    <p class="text-xs text-gray-500">{{ (mostCourses && mostCourses[0] && mostCourses[0].name) || 'N/A'
                        }}</p>
                </div>

                <div class="bg-gray-50 rounded-lg p-3">
                    <p class="text-sm text-gray-600">Lowest Course Load</p>
                    <p class="text-lg font-semibold text-gray-900">
                        {{ (leastCourses && leastCourses[0] && leastCourses[0].course_count) || 0 }} courses
                    </p>
                    <p class="text-xs text-gray-500">{{ (leastCourses && leastCourses[0] && leastCourses[0].name) ||
                        'N/A' }}</p>
                </div>

                <div class="bg-gray-50 rounded-lg p-3">
                    <p class="text-sm text-gray-600">Average Course Load</p>
                    <p class="text-lg font-semibold text-gray-900">
                        {{ averageCourseLoad }}
                    </p>
                    <p class="text-xs text-gray-500">courses per faculty</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "FacultyCoursesSummary",
    props: {
        mostCourses: {
            type: Array,
            default: () => []
        },
        leastCourses: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        error: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        averageCourseLoad() {
            if (!this.mostCourses || !this.leastCourses) return '0.0';

            const allCourses = [...this.mostCourses, ...this.leastCourses];
            if (allCourses.length === 0) return '0.0';

            const total = allCourses.reduce((sum, faculty) => sum + faculty.course_count, 0);
            return (total / allCourses.length).toFixed(1);
        }
    }
};
</script>

<style scoped>
/* Additional styling if needed */
</style>
